export const POSTHOG_EVENTS = {
    SCAN_UPLOAD_FAILED: 'scan.upload.failed',
    SCAN_CONTINUED_TO_BATCH: 'scan.continued_to_batch',
    TEMPLATE_TITLE_CREATED: 'template.title.created',
    TEMPLATE_TITLE_UPDATED: 'template.title.updated',
    TEMPLATE_DESCRIPTION_CREATED: 'template.description.created',
    TEMPLATE_DESCRIPTION_UPDATED: 'template.description.updated',
    EXPORT_LISTINGS_SUCCEEDED: 'export.listings.succeeded',
    ONBOARDING_STEP_WELCOME_VIEWED: 'onboarding.step_welcome.viewed',
    ONBOARDING_STEP_WELCOME_COMPLETED: 'onboarding.step_welcome.completed',
    ONBOARDING_STEP_ABOUT_MARKETPLACES_COMPLETED: 'onboarding.step_aboutMarketplaces.completed',
    ONBOARDING_STEP_ABOUT_YOURSELF_COMPLETED: 'onboarding.step_aboutYourself.completed',
    ONBOARDING_STEP_ORG_DETAILS_COMPLETED: 'onboarding.step_orgDetails.completed',
    ONBOARDING_STEP_PLAN_COMPLETED: 'onboarding.step_plan.completed',
    ONBOARDING_COMPLETED: 'onboarding.completed',
    COACHING_GETTING_STARTED_COMPLETED: 'coaching.getting_started.completed',
    CONNECTION_ADDED: 'connection.added',
    CARD_MARKET_PRICE_ADDED: 'card.marketprice.added',
    SUBSCRIPTION_UPGRADE_INITIATED: 'subscription_upgrade_initiated',
    SUBSCRIPTION_DOWNGRADE_INITIATED: 'subscription_downgrade_initiated',
    PAYWALL_VIEWED: 'paywall_viewed',
    // Insights paywall (Rookie gating). Each carries report_id, insight_id, plan and trigger
    // (INSIGHTS_UPGRADE_TRIGGERS): the report's gated rows were shown, the dealer asked to upgrade from
    // them, and that upgrade went through.
    INSIGHTS_PAYWALL_VIEWED: 'insights_paywall_viewed',
    INSIGHTS_UPGRADE_CLICKED: 'insights_upgrade_clicked',
    INSIGHTS_UPGRADE_COMPLETED: 'insights_upgrade_completed',
    USER_CREATE_ACCOUNT_VIEW: 'user_createAccount_view',
    USER_CREATE_ACCOUNT_SUBMITTED: 'user_createAccount_submitted',
    // Inventory redesign experiment (`inventory-beta`). PostHog stamps every
    // event with the user's variant, so these are what the experiment's
    // offer → opt-in → opt-out funnel is measured on.
    INVENTORY_BETA_OPTED_IN: 'inventory_beta.opted_in',
    INVENTORY_BETA_OPTED_OUT: 'inventory_beta.opted_out',
    // The offer was SEEN. Without it "saw it and didn't want it" and "never
    // found it" are the same shape in the data, and they need opposite fixes
    // Fires once per dealer, on the first entry point to render.
    INVENTORY_BETA_OFFER_SHOWN: 'inventory_beta.offer_shown',
    // The dealer has enough experience of the beta to have an opinion — a
    // session after the one they joined in, plus real work done in it. The
    // in-product survey triggers off this rather than off a
    // first-visit event, which asked people what they thought before they had
    // done anything.
    INVENTORY_BETA_SURVEY_ELIGIBLE: 'inventory_beta.survey_eligible',
    // The dealer became ELIGIBLE for enrollment — they did the thing that makes
    // the beta meaningful to them (first batch created, or cards imported).
    // Paired with the `inventory_beta_eligible` person property, which is what
    // the flag's release condition targets.
    INVENTORY_BETA_ELIGIBLE: 'inventory_beta.eligible',
}

/**
 * `inventory_beta_status` person property — which experience the dealer is in.
 * The opt-out survey targets on it, so the values are a contract with PostHog,
 * not a display string.
 */
export const INVENTORY_BETA_STATUS = {
    OPTED_IN: 'opted_in',
    OPTED_OUT: 'opted_out',
}

/**
 * `via` on `inventory_beta.eligible` — WHAT made them eligible.
 *
 * Batch creation only, for now: it is always client-initiated and
 * always has a response to hang the write off. Marketplace imports finish
 * server-side, sometimes after the dealer has closed the tab, so a client-side
 * trigger would miss them unpredictably — and a trigger that fires for some
 * dealers and not others is worse than one that fires for fewer. Enrollment
 * happens BEFORE randomisation, so anyone missed costs sample size, not
 * validity.
 */
export const INVENTORY_BETA_ELIGIBLE_VIA = {
    BATCH_CREATED: 'batch_created',
}

/**
 * `trigger` on `inventory_beta.survey_eligible` — the work that proved the
 * dealer has used the beta for real.
 */
export const INVENTORY_BETA_SURVEY_TRIGGER = {
    BATCH_CREATED: 'batch_created',
    CARD_LISTED: 'card_listed',
}

/**
 * Only dealers who signed up on or after this date are enrolled.
 *
 * 12,138 tenured dealers created a batch in the last 120 days; their retention
 * base rate is nothing like a new dealer's, and mixing them in makes the
 * retention comparison — the experiment's actual hypothesis — unreadable.
 *
 * Overridable per environment so the flag can be switched on independently of
 * the deploy: set `VITE_INVENTORY_BETA_LAUNCH_DATE` (any `Date`-parseable
 * string). Dev/staging set it to a past date so an existing account can
 * exercise the flow; on prod it stays the real launch date.
 *
 * The fallback is 2026-09-13, one day back from the ship date, so the dealers
 * who signed up while this was going out are in rather than stranded on the
 * wrong side of an hour boundary. It does NOT backfill: a day earlier only
 * admits a day's worth of signups, and every tenured dealer is still out.
 */
export const INVENTORY_BETA_LAUNCH_DATE =
    import.meta.env.VITE_INVENTORY_BETA_LAUNCH_DATE || '2026-09-13T00:00:00Z'

/**
 * `surface` on the `inventory_beta.*` events — WHICH control the dealer saw or
 * used. A closed enum, never free text: it is a breakdown dimension, and `from`
 * already showed what an unbounded value does to one (a raw path carrying batch
 * ids, so nearly every value is unique and it breaks down into noise).
 *
 * The first block is the agreed analytics vocabulary. The second is the
 * surfaces this app actually ships that the agreed list doesn't name — the two welcome-modal
 * stages above all, since "which prompt converts" is the question the two-stage
 * design exists to answer and `from` can't tell them apart (both only ever
 * appear on /inventory).
 */
export const INVENTORY_BETA_SURFACES = {
    // ── The agreed vocabulary ───────────────────────────────────────────────
    /** Avatar dropdown row — the primary entry point into the beta. */
    AVATAR_DROPDOWN: 'avatar_dropdown',
    /** Empty-state CTA on Inventory. Not built yet. */
    INVENTORY_EMPTY_STATE: 'inventory_empty_state',
    /** Any control in the Inventory page header — the "Try Inventory Beta"
     *  button beside the breadcrumb, and the "···" row that leaves the beta. */
    INVENTORY_HEADER: 'inventory_header',
    /** Batch detail page. Not built yet. */
    BATCH_DETAIL: 'batch_detail',
    /** Settings. Not built yet. */
    SETTINGS: 'settings',
    /** In-app notification. Not built yet. */
    NOTIFICATION: 'notification',
    /** Changelog entry. Not built yet — but `/changelog` already shows up as a
     *  `from` value, so the path in exists somewhere. */
    CHANGELOG: 'changelog',

    // ── Shipped surfaces the agreed list doesn't name ───────────────────────
    /** Welcome modal, first prompt. */
    WELCOME_MODAL_1: 'welcome_modal_1',
    /** Welcome modal, follow-up prompt after one "Maybe later". */
    WELCOME_MODAL_2: 'welcome_modal_2',
    /** "Inventory Beta is here!" card in the expanded sidebar. */
    SIDEBAR_PROMO: 'sidebar_promo',
    /** A call site that hasn't been instrumented — visible, not silent. */
    UNKNOWN: 'unknown',
}